import React, { useState, useEffect } from "react";
import { fetchTenants, createTenant } from "../../services/authService";
import Sidebar from "../shared/Sidebar";
import Header from "../shared/Header";
import LoadingSpinner from "../shared/LoadingSpinner";
import { TablerSearch } from "../shared/Icons";
import { useTheme } from "../../contexts/ThemeContext";
import { getItem, logout } from "../../utils/authStorage";
import { toast } from "react-toastify";
import "../../styles/addUserModal.css";
import "./TenantManagement.css";

function TenantManagement() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [domainFilter, setDomainFilter] = useState("ALL");
  const [showModal, setShowModal] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const username = getItem("username");
  const role = (getItem("role") || "admin").toLowerCase();
  const { isDarkMode } = useTheme();

  const handleSignOut = () => {
    logout();
  };

  const hasRoleFn = (allowedRoles) => {
    const storedRole = (getItem("role") || "").toLowerCase();
    return allowedRoles.flat().some(
      (r) => typeof r === "string" && r.toLowerCase() === storedRole
    );
  };

  // Form state
  const [displayName, setDisplayName] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [domain, setDomain] = useState("surveillance");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const loadTenants = async () => {
    try {
      setLoading(true);
      const data = await fetchTenants();
      setTenants(data || []);
    } catch (err) {
      console.error("Failed to load tenants:", err);
      toast.error("Failed to load tenants list.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, []);

  const handleNameChange = (e) => {
    const val = e.target.value;
    setDisplayName(val);
    const generatedSlug = val
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, "_")
      .replace(/_+/g, "_")
      .slice(0, 50);
    setTenantId(generatedSlug);
  };

  const validate = () => {
    const errs = {};
    if (!displayName.trim()) errs.displayName = "Display Name is required.";
    if (!tenantId.trim()) {
      errs.tenantId = "Tenant ID is required.";
    } else if (!/^[a-z0-9_]{1,55}$/.test(tenantId.trim())) {
      errs.tenantId = "Tenant ID must be lowercase alphanumeric/underscore (max 55 chars).";
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setIsSubmitting(true);
      const payload = {
        id: tenantId.trim().toLowerCase(),
        display_name: displayName.trim(),
        domain: domain,
      };

      const response = await createTenant(payload);
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const errorMsg =
          data?.id?.[0] ||
          data?.detail ||
          data?.error ||
          "Failed to create tenant. ID may already exist.";
        toast.error(errorMsg);
        return;
      }

      toast.success(`Tenant '${displayName}' created successfully!`);
      setShowModal(false);
      setDisplayName("");
      setTenantId("");
      setDomain("surveillance");
      loadTenants();
    } catch (err) {
      console.error("Failed to create tenant:", err);
      toast.error("Error creating tenant.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredTenants = tenants.filter((item) => {
    const matchesSearch =
      (item.display_name && item.display_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.id && item.id.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesDomain = domainFilter === "ALL" || item.domain === domainFilter;
    return matchesSearch && matchesDomain;
  });

  return (
    <div className={`tenant-management-page ${isDarkMode ? "dark" : "light"}`}>
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((prev) => !prev)}
        isDarkMode={isDarkMode}
        username={username}
        role={role}
        onLogout={handleSignOut}
        hasRole={hasRoleFn}
      />

      <div className="main-content">
        <Header title="Tenant Management" />
        <div className="main-divider" />

        <div className="tenant-page-container">
          <div className="tenant-list">
            <div className="page-header">
              <div className="header-left">
                <h3 className="tenant-list-title">Tenants & Organizations</h3>
                <p className="tenant-list-subtitle">
                  Manage multi-tenant organizations and their provisioned domains.
                </p>
              </div>
              <button className="add-tenant-btn" onClick={() => setShowModal(true)}>
                <span className="plus-icon">+</span> Add Tenant
              </button>
            </div>

            {/* Filter controls row */}
            <div className="filter-controls-row">
              <div className="search-input-wrapper">
                <TablerSearch className="search-icon" size={15} color="var(--text-muted)" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search tenant name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="filter-dropdowns-group">
                <select
                  className="filter-select domain-filter-select"
                  value={domainFilter}
                  onChange={(e) => setDomainFilter(e.target.value)}
                >
                  <option value="ALL">All Domains</option>
                  <option value="surveillance">Surveillance</option>
                  <option value="construction">Construction</option>
                </select>
              </div>
            </div>

            {loading ? (
              <LoadingSpinner message="Loading tenants..." />
            ) : (
              <div className="tenant-table-wrapper">
                <table className="tenant-table">
                  <thead>
                    <tr>
                      <th style={{ width: "25%" }}>Organization / Tenant</th>
                      <th style={{ width: "20%" }}>Tenant ID (Slug)</th>
                      <th style={{ width: "20%" }}>Schema Name</th>
                      <th style={{ width: "15%" }}>Domain</th>
                      <th style={{ width: "10%" }} className="center">Users</th>
                      <th style={{ width: "10%" }} className="center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTenants.map((tItem) => (
                      <tr key={tItem.id}>
                        <td>
                          <div className="tenant-identity">
                            <span className="tenant-display-name">{tItem.display_name}</span>
                          </div>
                        </td>
                        <td>
                          <code className="tenant-slug-code">{tItem.id}</code>
                        </td>
                        <td>
                          <span className="tenant-schema-text">{tItem.schema_name}</span>
                        </td>
                        <td>
                          <span
                            className={`domain-badge ${
                              tItem.domain === "construction"
                                ? "domain-construction"
                                : "domain-surveillance"
                            }`}
                          >
                            {tItem.domain || "surveillance"}
                          </span>
                        </td>
                        <td className="center">
                          <span className="user-count-badge">{tItem.user_count || 0}</span>
                        </td>
                        <td className="center">
                          <span className="status-pill active">Active</span>
                        </td>
                      </tr>
                    ))}
                    {filteredTenants.length === 0 && (
                      <tr>
                        <td colSpan={6} className="no-tenants-cell">
                          No tenants found. Click "+ Add Tenant" to create one.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Tenant Modal */}
      {showModal && (
        <div className="modal fade show d-block custom-modal" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.6)" }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <button
                type="button"
                className="custom-close-btn"
                onClick={() => setShowModal(false)}
                disabled={isSubmitting}
              >
                <span className="close-icon">&times;</span>
              </button>
              <div className="modal-body">
                <div className="modal-form-header">
                  <h3 className="modal-form-title">Create New Tenant</h3>
                  <p className="modal-form-subtitle">
                    Provision an isolated database schema and select its domain intent.
                  </p>
                </div>

                <form onSubmit={handleCreateTenant} className="tenant-create-form" style={{ padding: "0 1.5rem 1.5rem" }}>
                  <div className="form-group mb-3">
                    <label className="form-label">Organization / Display Name *</label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.displayName ? "is-invalid" : ""}`}
                      placeholder="e.g. Acme Corporation"
                      value={displayName}
                      onChange={handleNameChange}
                      required
                    />
                    {formErrors.displayName && (
                      <div className="invalid-feedback">{formErrors.displayName}</div>
                    )}
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label">
                      Tenant ID (Slug) *
                      <span className="form-hint"> (Used in DB schema: tenant_&lt;id&gt;)</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${formErrors.tenantId ? "is-invalid" : ""}`}
                      placeholder="e.g. acme"
                      value={tenantId}
                      onChange={(e) => setTenantId(e.target.value.toLowerCase())}
                      required
                    />
                    {formErrors.tenantId && (
                      <div className="invalid-feedback">{formErrors.tenantId}</div>
                    )}
                  </div>

                  <div className="form-group mb-4">
                    <label className="form-label">Assigned Domain *</label>
                    <select
                      className="form-control"
                      value={domain}
                      onChange={(e) => setDomain(e.target.value)}
                    >
                      <option value="surveillance">Surveillance & Monitoring</option>
                      <option value="construction">Construction</option>
                    </select>
                    <span className="form-hint">
                      {domain === "construction"
                        ? "Provisions ChatBot vector store, project management, and site data tables."
                        : "Provisions surveillance schema for field units, alerts, and live feeds."}
                    </span>
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="tenant-modal-btn tenant-modal-btn-cancel"
                      onClick={() => setShowModal(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="tenant-modal-btn tenant-modal-btn-submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Provisioning..." : "Create & Provision Tenant"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TenantManagement;
